import { openConsentPreferences } from "../../utils/consent";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Bot,
  Grid2X2,
  ShieldCheck,
  PanelLeftClose,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toolDefinitions } from "../../features/tools/registry";
import { usePinnedTools } from "../../features/tools/usePinnedTools";
import { useToolScope } from "../../features/toolExperience/useToolScope";
import { OrganizationSwitcher } from "../OrganizationSwitcher";
import DarkModeToggle from "../DarkModeToggle";
import { useModal } from "../../hooks/useModal";
import styles from "./SideNavigation.module.css";
interface SideNavigationProps {
  isCollapsed: boolean;
  showMobileMenu: boolean;
  onToggle: () => void;
  onClose: () => void;
  activeRoute: string;
  isMobile: boolean;
  className?: string;
}
export function SideNavigation({
  isCollapsed,
  showMobileMenu,
  onToggle,
  onClose,
  isMobile,
  className = "",
}: SideNavigationProps) {
  const { user } = useAuth(),
    scope = useToolScope(),
    location = useLocation();
  const pins = usePinnedTools(scope, user?.role);
  const { modalRef } = useModal<HTMLElement>(isMobile && showMobileMenu, {
    onClose,
  });
  const current = toolDefinitions.find(
    (t) =>
      location.pathname === t.path ||
      location.pathname.startsWith(t.path + "/"),
  );
  const compact = isCollapsed && !isMobile;
  const classes = [
    styles.sideNavigation,
    compact && styles.collapsed,
    isMobile && styles.mobile,
    isMobile && showMobileMenu && styles.mobileOpen,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const entry = (item: {
    id: string;
    label: string;
    path: string;
    icon: typeof Home;
  }) => {
    const active =
      location.pathname === item.path ||
      location.pathname.startsWith(item.path + "/");
    return (
      <Link
        key={item.id}
        to={item.path}
        className={`${styles.navItem} ${active ? styles.active : ""}`}
        onClick={isMobile ? onClose : undefined}
        aria-label={item.label}
        aria-current={active ? "page" : undefined}
        title={compact ? item.label : undefined}
      >
        <span className={styles.navItemIcon}>
          <item.icon size={22} aria-hidden="true" />
        </span>
        {!compact && <span className={styles.navItemLabel}>{item.label}</span>}
      </Link>
    );
  };
  if (isMobile && !showMobileMenu) return null;
  return (
    <nav
      className={classes}
      ref={modalRef}
      aria-label="Main navigation"
      role={isMobile ? "dialog" : undefined}
      aria-modal={isMobile ? true : undefined}
      tabIndex={isMobile ? -1 : undefined}
    >
      <div className={styles.navHeader}>
        {!compact && (
          <div className={styles.headerContent}>
            <h2 className={styles.navTitle}>Navigation</h2>
            <button
              type="button"
              className={isMobile ? styles.mobileClose : styles.collapseButton}
              onClick={isMobile ? onClose : onToggle}
              aria-label={isMobile ? "Close navigation" : "Collapse navigation"}
            >
              {isMobile ? <X size={22} /> : <PanelLeftClose size={18} />}
            </button>
          </div>
        )}
        {isMobile && (
          <div className={styles.mobileSettings}>
            <OrganizationSwitcher />
            <DarkModeToggle />
          </div>
        )}
      </div>
      <div className={styles.navItems}>
        {entry({
          id: "dashboard",
          label: "Home",
          path: "/dashboard",
          icon: Home,
        })}
        {!compact && <p className={styles.groupLabel}>Pinned tools</p>}
        {pins.ids
          .map((id) => toolDefinitions.find((t) => t.id === id))
          .filter((t): t is NonNullable<typeof t> => !!t)
          .map(entry)}
        {!pins.ids.length && !compact && (
          <p className={styles.groupLabel}>Pin tools from All tools.</p>
        )}
        {current && !pins.ids.includes(current.id) && (
          <>
            {!compact && <p className={styles.groupLabel}>Current tool</p>}
            {entry(current)}
          </>
        )}
        {entry({
          id: "tools",
          label: "All tools",
          path: "/tools",
          icon: Grid2X2,
        })}
        {entry({
          id: "agent",
          label: "Agent",
          path: "/agent",
          icon: Bot,
        })}
      </div>
      <div className={styles.navFooter}>
        <button
          type="button"
          className={styles.privacyLink}
          aria-label="Privacy preferences"
          onClick={() => {
            if (isMobile) onClose();
            openConsentPreferences();
          }}
        >
          {compact ? (
            <ShieldCheck size={20} aria-hidden="true" />
          ) : (
            "Privacy preferences"
          )}
        </button>
        {!compact && user && (
          <div className={styles.organizationInfo}>
            <div className={styles.orgName}>{user.name || user.email}</div>
            <div className={styles.orgPlan}>
              {user.role?.toUpperCase() || "MEMBER"}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
export default SideNavigation;
